import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PenLine, Loader2, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { fetchBlogs, createBlog } from "@/lib/api";

const formatDate = (d) => {
    try {
        return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch (e) {
        return "";
    }
};

const Blogs = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", author: "", excerpt: "", body: "" });
    const [hp, setHp] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            const data = await fetchBlogs();
            setPosts(data);
        } catch (e) {
            toast.error("Could not load blogs.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { title, author, excerpt, body } = form;
        if (title.trim().length < 3 || author.trim().length < 1 || excerpt.trim().length < 10 || body.trim().length < 20) {
            toast.error("Please complete all fields, body must be at least 20 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await createBlog({ ...form, hp });
            toast.success("Reflection submitted!", {
                description: "Your blog will appear after the parish reviews it.",
                duration: 6000,
            });
            setForm({ title: "", author: "", excerpt: "", body: "" });
            setHp("");
            setShowForm(false);
            await load();
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error(err.response.data?.detail || "Too many submissions. Please try again later.");
            } else {
                toast.error("Couldn't publish your post. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Parishioner Voices</span>
            <h1 className="heading-display mt-2">Parish Blogs</h1>
            <p className="mt-4 max-w-2xl text-maroon-700/85">
                Reflections, testimonies and stories of faith written by parishioners of Holy Trinity Kileleshwa.
                Share your own, keep it edifying, charitable, and rooted in the faith.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={() => setShowForm((s) => !s)}
                    data-testid="blogs-toggle-form"
                    className="btn-primary"
                >
                    <PenLine size={16} /> {showForm ? "Cancel" : "Write a blog"}
                </button>
                <Link to="/community" className="btn-ghost">
                    <BookOpen size={16} /> Back to Community Hub
                </Link>
            </div>

            {showForm && (
                <form
                    onSubmit={handleSubmit}
                    data-testid="blog-form"
                    className="mt-6 rounded-2xl shadow-lg p-5 sm:p-6 space-y-4 border"
                    style={{ background: "#252525", borderColor: "#900000", color: "#F3EDE4" }}
                >
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider text-maroon-700/80">Title</span>
                            <input
                                data-testid="blog-title"
                                value={form.title}
                                onChange={handleChange("title")}
                                required
                                maxLength={200}
                                className="mt-1 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 blog-input"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider text-maroon-700/80">Author</span>
                            <input
                                data-testid="blog-author"
                                value={form.author}
                                onChange={handleChange("author")}
                                required
                                maxLength={120}
                                className="mt-1 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 blog-input"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs uppercase tracking-wider text-maroon-700/80">Short summary</span>
                        <input
                            data-testid="blog-excerpt"
                            value={form.excerpt}
                            onChange={handleChange("excerpt")}
                            placeholder="A one-line summary that appears on the listing page."
                            required
                            maxLength={400}
                            className="mt-1 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 blog-input"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-wider text-maroon-700/80">Your reflection</span>
                        <textarea
                            data-testid="blog-body"
                            value={form.body}
                            onChange={handleChange("body")}
                            required
                            rows={8}
                            maxLength={20000}
                            placeholder="Write freely. Paragraphs are preserved."
                            className="mt-1 w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 blog-input resize-y"
                        />
                    </label>
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] text-maroon-700/70 italic">
                            Posts are reviewed by the parish before appearing publicly. Please be charitable.
                        </p>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="blog-submit"
                            className="btn-primary disabled:opacity-60"
                        >
                            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publish
                        </button>
                    </div>
                </form>
            )}

            <div data-testid="blogs-list" className="mt-10 grid gap-5">
                {loading && <p className="text-sm text-maroon-700/70">Loading reflections…</p>}
                {!loading && posts.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-maroon-200 bg-cream-50 p-8 text-center">
                        <p className="text-maroon-700/80">
                            No reflections yet. Be the first to share, <button onClick={() => setShowForm(true)} className="underline decoration-gold-400 underline-offset-4 hover:text-maroon-900">write a blog</button>.
                        </p>
                    </div>
                )}
                {posts.map((p) => (
                    <Link
                        to={`/blogs/${p.id}`}
                        key={p.id}
                        data-testid={`blog-card-${p.id}`}
                        className="block rounded-2xl border border-maroon-100 bg-white/80 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
                    >
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gold-500/80 font-semibold">
                            {formatDate(p.created_at)} · {p.author}
                        </div>
                        <h2 className="font-serif text-2xl text-maroon-800 mt-1">{p.title}</h2>
                        <p className="mt-2 text-sm text-maroon-700/85 leading-relaxed">{p.excerpt}</p>
                        <span className="mt-3 inline-block text-xs font-semibold text-maroon-700">Read more →</span>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default Blogs;
